import { NavLink, useLocation } from "react-router-dom";
import uadyLogo from "/img/navbar/uady_icon.png";
import { useState, useEffect, useRef } from "react";
import { useTranslation } from '../../../components/Translation';
import { navItems, languageOptions } from "../../../constants/navbar/navItems";
import { partnerLogos } from "../../../constants/navbar/partnerLogos";

export default function Navbar() {
  const location = useLocation();
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const [isLanguageDropdownOpen, setIsLanguageDropdownOpen] = useState(false);
  const [isPublicationsDropdownOpen, setIsPublicationsDropdownOpen] = useState(false);
  const [isMobilePublicationsOpen, setIsMobilePublicationsOpen] = useState(false);
  const [isMayanDropdownOpen, setIsMayanDropdownOpen] = useState(false);
  const [isMobileMayanOpen, setIsMobileMayanOpen] = useState(false);
  const [currentSlide, setCurrentSlide] = useState(0);
  const dropdownRef = useRef<HTMLDivElement>(null);
  const publicationsDropdownRef = useRef<HTMLLIElement>(null);
  const mayanDropdownRef = useRef<HTMLLIElement>(null);
  const { language, setLanguage, t } = useTranslation();

  useEffect(() => {
    const transitionDuration = 1000;
    const staticDuration = 3000;
    let timeoutId: ReturnType<typeof setTimeout>;
    const showNextSlide = () => {
      setCurrentSlide((prev) => (prev + 1) % partnerLogos.length);
      timeoutId = setTimeout(showNextSlide, transitionDuration + staticDuration);
    };
    timeoutId = setTimeout(showNextSlide, staticDuration);
    return () => clearTimeout(timeoutId);
  }, []);

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target as Node)) {
        setIsLanguageDropdownOpen(false);
      }
      if (publicationsDropdownRef.current && !publicationsDropdownRef.current.contains(event.target as Node)) {
        setIsPublicationsDropdownOpen(false);
      }
      if (mayanDropdownRef.current && !mayanDropdownRef.current.contains(event.target as Node)) {
        setIsMayanDropdownOpen(false);
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  useEffect(() => {
    setIsMenuOpen(false);
    setIsPublicationsDropdownOpen(false);
    setIsMobilePublicationsOpen(false);
    setIsMayanDropdownOpen(false);
    setIsMobileMayanOpen(false);
  }, [location.pathname]);

  useEffect(() => {
    if (!isPublicationsDropdownOpen && !isMayanDropdownOpen) return;
    const handleEscape = (event: KeyboardEvent) => {
      if (event.key === "Escape") {
        setIsPublicationsDropdownOpen(false);
        setIsMayanDropdownOpen(false);
      }
    };
    document.addEventListener("keydown", handleEscape);
    return () => document.removeEventListener("keydown", handleEscape);
  }, [isPublicationsDropdownOpen, isMayanDropdownOpen]);

  const isHome = location.pathname === "/";
  const publicationsActive = ["/publications", "/researchLines"].includes(location.pathname);

  const getNavLinkClass = (isActive: boolean) =>
    [
      "px-3 py-2 rounded transition-colors",
      isActive ? "text-dorado-uady font-semibold border-b-2 border-dorado-uady" : "hover:text-dorado-uady",
    ].join(" ");

  return (
    <nav className={`w-full z-200 ${isHome ? "absolute top-0 left-0" : ""} bg-[#0d1f35]/95 backdrop-blur-sm text-white`}>
      <div className="mx-auto px-6 py-4 xl:px-16 xl:py-4 flex justify-between items-center">
        {/* Logo + brand name */}
        <div className="flex items-center gap-4">
          <a href="/" className="flex items-center gap-3">
            <img src={uadyLogo} alt="UADY Logo" className="h-9 w-9" />
            <span className="font-bold text-mini hidden md:block leading-tight">
              Laboratorio de Robótica<br />
              <span className="text-dorado-uady">e Industria 4.0</span>
            </span>
          </a>
        </div>

        {/* Desktop Nav */}
        <ul className="hidden lg:flex items-center space-x-1 font-medium text-mini">
          {navItems.map((item) => "children" in item ? (
            <li
              key={item.label}
              ref={item.label === "publicaciones" ? publicationsDropdownRef : mayanDropdownRef}
              className="group relative"
            >
              {item.label === "publicaciones" ? (
                <button
                  type="button"
                  aria-expanded={isPublicationsDropdownOpen}
                  aria-controls={`desktop-${item.label}-menu`}
                  onClick={() => setIsPublicationsDropdownOpen(open => !open)}
                  className={`${getNavLinkClass(publicationsActive)} inline-flex items-center gap-1`}
                >
                  {t(`laboratorio.${item.label}`)}
                  <svg aria-hidden="true" viewBox="0 0 20 20" fill="none" className={`h-3.5 w-3.5 transition-transform ${isPublicationsDropdownOpen ? "rotate-180" : ""}`}>
                    <path d="m5 7.5 5 5 5-5" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
                  </svg>
                </button>
              ) : (
                <div className="flex items-center">
                  <NavLink
                    to="/mayanOnChip"
                    className={getNavLinkClass(location.pathname === "/mayanOnChip")}
                  >
                    {t(`laboratorio.${item.label}`)}
                  </NavLink>
                  <button
                    type="button"
                    aria-label={t("laboratorio.mayan_menu")}
                    aria-expanded={isMayanDropdownOpen}
                    aria-controls={`desktop-${item.label}-menu`}
                    onClick={() => setIsMayanDropdownOpen(open => !open)}
                    className="rounded p-2 transition-colors hover:text-dorado-uady focus:outline-none focus:ring-2 focus:ring-dorado-uady"
                  >
                    <svg aria-hidden="true" viewBox="0 0 20 20" fill="none" className={`h-3.5 w-3.5 transition-transform ${isMayanDropdownOpen ? "rotate-180" : ""}`}>
                      <path d="m5 7.5 5 5 5-5" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
                    </svg>
                  </button>
                </div>
              )}
              <div
                className={`absolute left-0 top-full z-50 w-56 pt-2 ${item.label === "publicaciones"
                  ? isPublicationsDropdownOpen ? "block" : "hidden group-hover:block group-focus-within:block"
                  : isMayanDropdownOpen ? "block" : "hidden"}`}
              >
                <ul id={`desktop-${item.label}-menu`} className="overflow-hidden rounded-xl border border-white/10 bg-[#192e4c] p-2 shadow-xl">
                  {item.children.map(child => (
                    <li key={child.pathname}>
                      {child.external ? (
                        <a
                          href={child.pathname}
                          target="_blank"
                          rel="noopener noreferrer"
                          onClick={() => setIsMayanDropdownOpen(false)}
                          className="block rounded-lg px-3 py-2.5 text-white/90 transition-colors hover:bg-white/10 hover:text-dorado-uady"
                        >
                          {t(`laboratorio.${child.label}`)}
                        </a>
                      ) : (
                        <NavLink
                          to={child.pathname}
                          onClick={() => {
                            setIsPublicationsDropdownOpen(false);
                            setIsMayanDropdownOpen(false);
                          }}
                          className={({ isActive }) => `block rounded-lg px-3 py-2.5 transition-colors ${isActive ? "bg-white/10 text-dorado-uady font-semibold" : "text-white/90 hover:bg-white/10 hover:text-dorado-uady"}`}
                        >
                          {t(`laboratorio.${child.label}`)}
                        </NavLink>
                      )}
                    </li>
                  ))}
                </ul>
              </div>
            </li>
          ) : (
            <li key={item.pathname}>
              <NavLink to={item.pathname} className={({ isActive }) => getNavLinkClass(isActive)}>
                {t(`laboratorio.${item.label}`)}
              </NavLink>
            </li>
          ))}
        </ul>

        {/* Right side: partner logo carousel + lang + cursos button */}
        <div className="hidden lg:flex items-center gap-4">
          {/* Partner logo mini carousel */}
          {/* Óvalo difuminado: el fondo se desvanece hacia los bordes en lugar de una caja marcada */}
          <div className="h-8 w-24 relative px-2 py-1">
            {/* Capa del óvalo, más grande que la caja para que el color se extienda sin agrandar los logos */}
            <div
              aria-hidden="true"
              className="pointer-events-none absolute -inset-x-8 -inset-y-4"
              style={{ background: "radial-gradient(closest-side, rgba(255,255,255,0.4), rgba(255,255,255,0.2) 55%, transparent)" }}
            />
  {partnerLogos.map((logo, index) => (
    <img
      key={index}
      src={logo.src}
      alt={logo.alt}
      className={`h-full w-auto object-contain absolute inset-0 m-auto transition-all duration-700 ${
        currentSlide === index ? "opacity-100" : "opacity-0"
      }`}
    />
  ))}
</div>

          {/* Language */}
          <div className="relative" ref={dropdownRef}>
            <button
              onClick={() => setIsLanguageDropdownOpen(!isLanguageDropdownOpen)}
              className="flex items-center gap-1 text-mini hover:text-dorado-uady transition-colors"
            >
              <span className="uppercase font-medium">{language}</span>
              <svg className={`w-3 h-3 transition-transform ${isLanguageDropdownOpen ? "rotate-180" : ""}`} fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
              </svg>
            </button>
            {isLanguageDropdownOpen && (
              <div className="absolute right-0 mt-2 w-16 bg-[#192e4c] rounded shadow-lg z-50">
                {languageOptions.map((lang) => (
                  <button
                    key={lang.code}
                    onClick={() => { setLanguage(lang.code); setIsLanguageDropdownOpen(false); }}
                    className={`block w-full text-left px-3 py-2 text-mini transition-colors ${language === lang.code ? "text-dorado-uady font-semibold" : "hover:text-dorado-uady"
                      }`}
                  >
                    {lang.name}
                  </button>
                ))}
              </div>
            )}
          </div>

          {/* Cursos CTA */}
          <NavLink
            to="https://mayanonchip.industria4fiuady.com/login/index.php"
            className="bg-dorado-uady text-white text-mini font-semibold px-4 py-2 rounded hover:bg-yellow-600 transition-colors"
          >
            {t("laboratorio.cursos")}
          </NavLink>
        </div>

        {/* Mobile hamburger */}
        <button
          type="button"
          aria-label={t(`laboratorio.${isMenuOpen ? "close_navigation_menu" : "navigation_menu"}`)}
          aria-expanded={isMenuOpen}
          aria-controls="mobile-navigation-menu"
          className="lg:hidden flex flex-col justify-center items-center w-8 h-8 space-y-1.5 cursor-pointer"
          onClick={() => setIsMenuOpen(!isMenuOpen)}
        >
          <span className={`block w-6 h-0.5 bg-dorado-uady rounded transition-all duration-300 ${isMenuOpen ? "rotate-45 translate-y-2" : ""}`} />
          <span className={`block w-6 h-0.5 bg-dorado-uady rounded transition-all duration-300 ${isMenuOpen ? "opacity-0" : ""}`} />
          <span className={`block w-6 h-0.5 bg-dorado-uady rounded transition-all duration-300 ${isMenuOpen ? "-rotate-45 -translate-y-2" : ""}`} />
        </button>
      </div>

      {/* Mobile menu */}
      <div id="mobile-navigation-menu" className={`lg:hidden transition-all duration-300 ${isMenuOpen ? "max-h-[80vh] overflow-y-auto" : "max-h-0 overflow-hidden"} bg-[#0d1f35]`}>
        <ul className="flex flex-col py-4 px-6 space-y-2">
          {navItems.map((item) => "children" in item ? (
            <li key={item.label}>
              <button
                type="button"
                aria-expanded={item.label === "publicaciones" ? isMobilePublicationsOpen : isMobileMayanOpen}
                aria-controls={`mobile-${item.label}-menu`}
                onClick={() => item.label === "publicaciones"
                  ? setIsMobilePublicationsOpen(open => !open)
                  : setIsMobileMayanOpen(open => !open)}
                className={`flex w-full items-center justify-between rounded px-3 py-2 text-left text-mini transition-colors ${(item.label === "publicaciones" ? publicationsActive : location.pathname === "/mayanOnChip") ? "text-dorado-uady font-semibold" : "hover:text-dorado-uady"}`}
              >
                {t(`laboratorio.${item.label}`)}
                <svg aria-hidden="true" viewBox="0 0 20 20" fill="none" className={`h-4 w-4 transition-transform ${(item.label === "publicaciones" ? isMobilePublicationsOpen : isMobileMayanOpen) ? "rotate-180" : ""}`}>
                  <path d="m5 7.5 5 5 5-5" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
                </svg>
              </button>
              {(item.label === "publicaciones" ? isMobilePublicationsOpen : isMobileMayanOpen) && (
                <ul id={`mobile-${item.label}-menu`} className="ml-3 border-l border-white/15 pl-3">
                  {item.children.map(child => (
                    <li key={child.pathname}>
                      {child.external ? (
                        <a
                          href={child.pathname}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="block rounded px-3 py-2 text-mini text-white/80 transition-colors hover:text-dorado-uady"
                          onClick={() => setIsMenuOpen(false)}
                        >
                          {t(`laboratorio.${child.label}`)}
                        </a>
                      ) : (
                        <NavLink
                          to={child.pathname}
                          className={({ isActive }) => `block rounded px-3 py-2 text-mini transition-colors ${isActive ? "text-dorado-uady font-semibold" : "text-white/80 hover:text-dorado-uady"}`}
                          onClick={() => setIsMenuOpen(false)}
                        >
                          {t(`laboratorio.${child.label}`)}
                        </NavLink>
                      )}
                    </li>
                  ))}
                </ul>
              )}
            </li>
          ) : (
            <li key={item.pathname}>
              <NavLink
                to={item.pathname}
                className={({ isActive }) =>
                  `block py-2 px-3 rounded text-mini transition-colors ${isActive ? "text-dorado-uady font-semibold" : "hover:text-dorado-uady"}`
                }
                onClick={() => setIsMenuOpen(false)}
              >
                {t(`laboratorio.${item.label}`)}
              </NavLink>
            </li>
          ))}
          <li>
            <NavLink to="https://mayanonchip.industria4fiuady.com/login/index.php" className="block py-2 px-3 bg-dorado-uady text-white text-mini font-semibold rounded mt-2 text-center" onClick={() => setIsMenuOpen(false)}>
              {t("laboratorio.cursos")}
            </NavLink>
          </li>
          <li className="flex gap-3 pt-2">
            {languageOptions.map((lang) => (
              <button
                key={lang.code}
                onClick={() => setLanguage(lang.code)}
                className={`px-3 py-1 rounded text-mini ${language === lang.code ? "bg-dorado-uady text-white" : "bg-white/10"}`}
              >
                {lang.code}
              </button>
            ))}
          </li>
        </ul>
      </div>
    </nav>
  );
}
